import { DocumentType, graphql } from '../gql';
import { useApiMutation, useApiQuery } from '../query/hooks';
import { Grid, InputLabel, Stack } from '@mui/material';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';
import { ModalContent } from '../modal/components/ModalContent';
import { ModalActions } from '../modal/components/ModalActions';
import { useModalContext } from '../modal/ModalContext';
import { useSnackbar } from '../store/snackbar';
import { mutationResponseHandler } from '../utils/mutationResponseHandler';
import { CreatableSelect } from '../connections/metrics/CreatableSelect';

const actionAddOpportunityModal_listOpportunities = graphql(`
	query actionAddOpportunityModal_listOpportunities {
		listOpportunity(filter: { status: { notEq: resolved } }) {
			id
			name
		}
	}
`);

const actionAddOpportunityModal_getAction = graphql(`
	query actionAddOpportunityModal_getAction($id: UUID!) {
		getAction(by: { id: $id }) {
			opportunities {
				id
			}
		}
	}
`);

const actionAddOpportunityModal_createOpportunity = graphql(`
	mutation actionAddOpportunityModal_createOpportunity(
		$name: String!
		$description: String
		$benefit: Int
		$status: OpportunityStatus!
		$links: Json!
		$activities: [OpportunityCreateActivitiesEntityRelationInput!]
	) {
		createOpportunity(
			data: { name: $name, description: $description, benefit: $benefit, status: $status, links: $links, activities: $activities }
		) {
			ok
			errorMessage
			node {
				id
			}
		}
	}
`);

const actionAddOpportunityModal_updateActionOpportunities = graphql(`
	mutation actionAddOpportunityModal_updateActionOpportunities($actionId: UUID!, $opportunityId: UUID!) {
		updateAction(by: { id: $actionId }, data: { opportunities: { connect: { id: $opportunityId } } }) {
			ok
			errorMessage
		}
	}
`);

export const addOpportunityModalSchema = z.object({
	actionId: z.string(),
	activityId: z.string().optional(),
});

export const AddOpportunityModal: FC<z.infer<typeof addOpportunityModalSchema>> = ({ actionId, activityId }) => {
	const intl = useIntl();
	const { closeModal } = useModalContext();
	const { openSnackbar } = useSnackbar();

	const { isLoading: isOpportunitiesLoading, data: opportunitiesData } = useApiQuery(actionAddOpportunityModal_listOpportunities);
	const { isLoading: isActionLoading, data: actionData } = useApiQuery(actionAddOpportunityModal_getAction, {
		variables: { id: actionId },
	});
	const { mutate: createOpportunity, isPending: isCreatePending } = useApiMutation(actionAddOpportunityModal_createOpportunity);
	const { mutate: updateActionOpportunities, isPending: isUpdatePending } = useApiMutation(
		actionAddOpportunityModal_updateActionOpportunities,
	);

	const [opportunityIdValue, setOpportunityIdValue] = useState<string>('');

	const removedIds = actionData?.getAction?.opportunities.map((s) => s.id) ?? [];
	const options = (opportunitiesData?.listOpportunity ?? [])
		.filter((s) => !isActionLoading && !removedIds.includes(s.id))
		.map((s) => ({ label: s.name, value: s.id }));

	const isLoading = isOpportunitiesLoading || isActionLoading;
	const submitDisabled = opportunityIdValue === '' || isLoading || isCreatePending || isUpdatePending;

	const onSubmit = () => {
		updateActionOpportunities(
			{
				actionId,
				opportunityId: opportunityIdValue,
			},
			{
				onSuccess: (result) => {
					mutationResponseHandler({
						result: result.updateAction.ok,
						toastMessages: {
							success: intl.formatMessage({ id: 'action.add-opportunity.update.success' }),
							error: intl.formatMessage({ id: 'action.add-opportunity.update.error' }),
						},
						callback: () => closeModal(),
						openSnackbar,
					});
				},
			},
		);
	};

	const handleOnCreate = (name: string) => {
		createOpportunity(
			{
				name,
				description: '',
				benefit: 5,
				status: 'new',
				links: { values: [] },
				...(activityId != null && {
					activities: {
						connect: {
							id: activityId,
						},
					},
				}),
			},
			{
				onSuccess: handleSuccessfulCreate,
			},
		);
	};

	const handleSuccessfulCreate = (data: DocumentType<typeof actionAddOpportunityModal_createOpportunity>) => {
		if (data.createOpportunity.ok) {
			setOpportunityIdValue(data.createOpportunity.node?.id ?? '');
		} else {
			mutationResponseHandler({
				result: false,
				toastMessages: { error: intl.formatMessage({ id: 'opportunity.list.create.error' }) },
				openSnackbar,
			});
		}
	};

	return (
		<>
			<ModalContent title={intl.formatMessage({ id: 'action.add-opportunity.title' })} description={''}>
				<Grid container spacing={3}>
					<Grid item xs={12}>
						<Stack spacing={1}>
							<InputLabel>
								<FormattedMessage id={'action.add-opportunity.form.label'} />
							</InputLabel>
							<CreatableSelect
								onChange={setOpportunityIdValue}
								loading={isLoading || isCreatePending}
								value={opportunityIdValue}
								items={options}
								createItemTitle={intl.formatMessage({ id: 'action.add-opportunity.form.create-placeholder' })}
								createDialogTitle={intl.formatMessage({ id: 'action.add-opportunity.popover.title' })}
								emptyItemErrorMessage={intl.formatMessage({ id: 'action.add-opportunity.popover.error.empty' })}
								placeholder={intl.formatMessage({ id: 'action.add-opportunity.form.select-placeholder' })}
								onCreate={handleOnCreate}
							/>
						</Stack>
					</Grid>
				</Grid>
			</ModalContent>
			<ModalActions
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={onSubmit}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={intl.formatMessage({ id: 'common.add' })}
				disablePrimaryButton={submitDisabled}
			/>
		</>
	);
};
